const animationSelector = '[data-animation]:not([data-animation="no-animation"])';
const VIEWPORT_THRESHOLD = 0.1;
const EARLY_ACTIVATION_OFFSET = 0.05;

const DEFAULT_LCP_SELECTORS = ['h1', '.hero', '.hero-image', '.main-title', '.page-header', '.hero-section'];

const LCP_CONFIG = {
    viewport: 1.0,
    minWidth: 0,
    minHeight: 0,
};

const getAnimationFrame = () => {
    if (typeof window === 'undefined') {
        return (callback) => setTimeout(callback, 16);
    }
    return window.requestAnimationFrame ? window.requestAnimationFrame.bind(window) : (callback) => setTimeout(callback, 16);
};

const getLcpSelectors = (() => {
    let cachedSelectors = null;

    return () => {
        if (cachedSelectors) {
            return cachedSelectors;
        }

        if (typeof window === 'undefined') {
            cachedSelectors = DEFAULT_LCP_SELECTORS;
            return cachedSelectors;
        }

        const rootStyle = window.getComputedStyle(document.documentElement);
        const rawSelectors = (rootStyle.getPropertyValue('--animation-system-lcp-selectors') || '').trim();
        const parsed = rawSelectors
            .split(',')
            .map((selector) => selector.trim())
            .filter(Boolean);

        cachedSelectors = parsed.length ? parsed : DEFAULT_LCP_SELECTORS;
        return cachedSelectors;
    };
})();

const isIdentityTransform = (value) => {
    if (!value || value === 'none') {
        return true;
    }

    if (value.startsWith('matrix3d(')) {
        const components = value.slice(9, -1).split(',').map(parseFloat);
        if (components.length !== 16) {
            return false;
        }

        return (
            Math.abs(components[0] - 1) < 1e-3 &&
            Math.abs(components[5] - 1) < 1e-3 &&
            Math.abs(components[10] - 1) < 1e-3 &&
            Math.abs(components[15] - 1) < 1e-3 &&
            components.every((component, index) => {
                if ([0, 5, 10, 15].includes(index)) {
                    return true;
                }
                return Math.abs(component) < 1e-3;
            })
        );
    }

    if (value.startsWith('matrix(')) {
        const components = value.slice(7, -1).split(',').map(parseFloat);
        if (components.length !== 6) {
            return false;
        }

        const [a, b, c, d, e, f] = components;
        return (
            Math.abs(a - 1) < 1e-3 &&
            Math.abs(d - 1) < 1e-3 &&
            Math.abs(b) < 1e-3 &&
            Math.abs(c) < 1e-3 &&
            Math.abs(e) < 1e-3 &&
            Math.abs(f) < 1e-3
        );
    }

    return false;
};

const cleanupIdentityTransform = (element) => {
    const {transform} = window.getComputedStyle(element);
    if (isIdentityTransform(transform)) {
        element.style.removeProperty('transform');
    }
};

const registerAnimationCleanup = (element) => {
    if (element.dataset.animationCleanupRegistered === 'true') {
        return;
    }

    element.addEventListener('animationend', (event) => {
        if (event.target !== element) {
            return;
        }
        cleanupIdentityTransform(element);
    });
    element.dataset.animationCleanupRegistered = 'true';
};

const prepareElement = (element) => {
    element.classList.remove('is-visible');
    delete element.dataset.animationState;
};

const playElement = (element) => {
    element.classList.add('is-visible');
    element.dataset.animationState = 'played';
    registerAnimationCleanup(element);
};

const resetElement = (element) => {
    element.classList.remove('is-visible');
    delete element.dataset.animationState;
};

const matchesLcpSelector = (element) => getLcpSelectors().some((selector) => element.matches(selector));

const isLCPCritical = (element) => {
    if (matchesLcpSelector(element)) {
        return true;
    }

    const rect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;

    return rect.top <= viewportHeight * LCP_CONFIG.viewport
        && rect.width >= LCP_CONFIG.minWidth
        && rect.height >= LCP_CONFIG.minHeight;
};

const markAsLCPCritical = (element) => {
    element.dataset.lcpCritical = 'true';
    element.classList.add('is-visible');
    element.dataset.animationState = 'played';
    registerAnimationCleanup(element);
};

const isInViewport = (element, threshold = VIEWPORT_THRESHOLD, earlyOffset = EARLY_ACTIVATION_OFFSET) => {
    const rect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const topLimit = -viewportHeight * threshold;
    const bottomLimit = viewportHeight * (1 - threshold + earlyOffset);
    return rect.top <= bottomLimit && rect.bottom >= topLimit;
};

export const initCssAnimationPlayer = ({allowRepeat = false} = {}) => {
    if (typeof window === 'undefined') {
        return;
    }

    const prefersReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
        return;
    }

    const elements = Array.from(document.querySelectorAll(animationSelector));
    if (!elements.length) {
        return;
    }

    const schedule = getAnimationFrame();
    const animationTargets = [];

    // READ-Phase: Alle Elemente klassifizieren (keine DOM-Writes!)
    const lcpElements = [];
    elements.forEach((element) => {
        if (isLCPCritical(element)) {
            lcpElements.push(element);
        } else {
            animationTargets.push(element);
        }
    });

    // WRITE-Phase: Alle DOM-Änderungen batched
    lcpElements.forEach(markAsLCPCritical);
    animationTargets.forEach(prepareElement);
    document.documentElement.classList.add('animations-ready');

    if (!animationTargets.length) {
        return;
    }

    const activateVisibleElements = () => {
        // READ-Phase: Sichtbarkeit aller Elemente prüfen
        const toActivate = animationTargets.filter((element) => {
            if (!allowRepeat && element.dataset.animationState === 'played') {
                return false;
            }
            return isInViewport(element);
        });

        // WRITE-Phase: Alle sichtbaren Elemente aktivieren
        toActivate.forEach(playElement);
    };

    schedule(() => {
        activateVisibleElements();
        window.addEventListener('load', () => schedule(activateVisibleElements), {once: true});
    });

    if (!('IntersectionObserver' in window)) {
        animationTargets.forEach((element) => {
            if (!allowRepeat && element.dataset.animationState === 'played') {
                return;
            }
            playElement(element);
        });
        return;
    }

    const pendingTargets = new Map();
    let observer;
    let flushHandle = null;

    const flushPendingTargets = () => {
        pendingTargets.forEach((isIntersecting, target) => {
            pendingTargets.delete(target);

            if (isIntersecting) {
                if (!allowRepeat && target.dataset.animationState === 'played') {
                    observer.unobserve(target);
                    return;
                }

                playElement(target);

                if (!allowRepeat) {
                    observer.unobserve(target);
                }
            } else if (allowRepeat) {
                resetElement(target);
            }
        });

        flushHandle = null;
    };

    const queueFlush = () => {
        if (flushHandle !== null) {
            return;
        }
        flushHandle = schedule(flushPendingTargets);
    };

    observer = new IntersectionObserver((entries) => {
        entries.forEach(({target, isIntersecting}) => {
            pendingTargets.set(target, isIntersecting);
        });
        queueFlush();
    }, {
        rootMargin: `0px 0px ${EARLY_ACTIVATION_OFFSET * 100}% 0px`,
        threshold: VIEWPORT_THRESHOLD,
    });

    animationTargets.forEach((element) => {
        if (!allowRepeat && element.dataset.animationState === 'played') {
            return;
        }

        observer.observe(element);
    });

    // Ensure any initial intersections recorded before RAF flush happen are processed promptly.
    queueFlush();
};
